// Grouping + compare logic for a seller's competing offers. Pure helpers, no
// React — the seller's offer list is flat (every offer across every listing
// mixed together), so we group it client-side by property to surface the offers
// that are actually bidding against each other on the same home.

import { displayStatus } from '../utils';

// An offer still in play: the seller can still accept, counter, or reject it, so
// it competes with the others. Accepted is handled separately (it's the winner),
// and rejected/withdrawn/expired/closed/deal_ended are out of the running.
const LIVE_STATUSES = new Set(['pending', 'countered']);

export function isLiveOffer(offer) {
  return LIVE_STATUSES.has(displayStatus(offer));
}

/**
 * Group offers by the property they're on, preserving first-seen order so the
 * caller's sort (newest, price, …) still decides the order of the groups and of
 * the offers within each group. Offers with no `property_id` fall back to their
 * address, and then to their own id, so they never collapse together by mistake.
 *
 * @param {Array<object>} offers
 * @returns {Array<{ key: string, propertyId: (number|null), propertyAddress: string, offers: Array<object> }>}
 */
export function groupOffersByProperty(offers = []) {
  const groups = [];
  const byKey = new Map();
  for (const offer of offers) {
    const key =
      offer.property_id != null
        ? `id:${offer.property_id}`
        : offer.property_address
          ? `addr:${offer.property_address}`
          : `offer:${offer.id}`;
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        propertyId: offer.property_id ?? null,
        propertyAddress: offer.property_address || '',
        offers: [],
      };
      byKey.set(key, group);
      groups.push(group);
    }
    group.offers.push(offer);
  }
  return groups;
}

/**
 * Compare stats for one property's offers, for the group header and the
 * "Highest" badge. The leader is the highest-priced offer still in contention
 * (the accepted one once a deal is struck, otherwise the highest live bid).
 *
 * @param {Array<object>} offers
 * @returns {{
 *   total: number, liveCount: number, acceptedId: (number|null),
 *   leaderId: (number|null), highest: (number|null), lowest: (number|null),
 *   spread: (number|null)
 * }}
 */
export function summarizeCompetingGroup(offers = []) {
  const live = offers.filter(isLiveOffer);
  const accepted = offers.find((o) => displayStatus(o) === 'accepted') || null;

  // Who's on top: the winner if one was chosen, else the highest live bid.
  const contenders = accepted ? [accepted] : live;
  let leader = null;
  for (const o of contenders) {
    if (leader == null || (Number(o.offer_price) || 0) > (Number(leader.offer_price) || 0)) {
      leader = o;
    }
  }

  const livePrices = live.map((o) => Number(o.offer_price) || 0);
  const highest = livePrices.length ? Math.max(...livePrices) : null;
  const lowest = livePrices.length ? Math.min(...livePrices) : null;

  return {
    total: offers.length,
    liveCount: live.length,
    acceptedId: accepted ? accepted.id : null,
    leaderId: leader ? leader.id : null,
    highest,
    lowest,
    spread: highest != null && lowest != null ? highest - lowest : null,
  };
}

/**
 * A group earns the competing-offers treatment (header, compare bar, cue) once
 * it holds more than one offer on the same property. A lone offer renders as an
 * ordinary card — there's nothing to compare it against.
 */
export function isCompetingGroup(group) {
  return !!group && Array.isArray(group.offers) && group.offers.length >= 2;
}

/**
 * Split grouped offers into the competing properties (shown as grouped sections)
 * and the single-offer properties (shown as ordinary cards), preserving the
 * incoming order.
 *
 * `offers` is the visible (filtered + sorted) set that gets displayed. The
 * optional `allOffers` is the full, unfiltered set: when given, whether a
 * property competes and the group's headline numbers are taken from it, so a
 * status filter can't understate a listing's competition (e.g. hiding a
 * countered rival while the "accepting ends the rest" cue still shows). Each
 * competing entry carries `offers` (what to render) and `summaryOffers` (what to
 * count). With no `allOffers`, the two are the same.
 *
 * @param {Array<object>} offers visible, sorted offers
 * @param {Array<object>} [allOffers] full, unfiltered offers
 * @returns {{ competing: Array<object>, singles: Array<object> }}
 */
export function partitionCompetingOffers(offers = [], allOffers = null) {
  const trueByKey = new Map();
  for (const group of groupOffersByProperty(allOffers || offers)) {
    trueByKey.set(group.key, group);
  }

  const competing = [];
  const singles = [];
  for (const group of groupOffersByProperty(offers)) {
    const truth = trueByKey.get(group.key) || group;
    if (isCompetingGroup(truth)) {
      competing.push({ ...group, summaryOffers: truth.offers });
    } else {
      singles.push(group.offers[0]);
    }
  }
  return { competing, singles };
}
